<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import IssueClusterMap from './IssueClusterMap.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const STATUSES = ['open', 'monitoring', 'dispatched', 'resolved', 'closed'];

const statusFilter = ref('');
const viewMode = ref('list');

const clusters = computed(() => store.getters['issueClusters/getIssueClusters']);
const uiFlags = computed(() => store.getters['issueClusters/getUIFlags']);

const mapPoints = computed(() =>
  clusters.value
    .filter(cluster => cluster.centroid_lat && cluster.centroid_lng)
    .map(cluster => ({
      lat: cluster.centroid_lat,
      lng: cluster.centroid_lng,
      label: `#${cluster.display_id} — ${t('ISSUE_CLUSTERS.LIST.REPORTS', { count: cluster.reports_count })}`,
    }))
);

const fetchClusters = () => {
  const params = statusFilter.value ? { status: statusFilter.value } : {};
  store.dispatch('issueClusters/fetch', params);
};

const onStatusChange = () => fetchClusters();

const openCluster = cluster => {
  router.push(accountScopedRoute('issue_cluster_detail', { clusterId: cluster.id }));
};

const goToSuggestions = () => {
  router.push(accountScopedRoute('issue_cluster_suggestions'));
};

onMounted(fetchClusters);
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 overflow-auto">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('ISSUE_CLUSTERS.HEADER_TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('ISSUE_CLUSTERS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        variant="outline"
        color="slate"
        size="sm"
        :label="t('ISSUE_CLUSTERS.TABS.SUGGESTIONS')"
        icon="i-lucide-inbox"
        @click="goToSuggestions"
      />
    </div>

    <div class="flex items-center gap-3 mb-4">
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        {{ t('ISSUE_CLUSTERS.FILTERS.STATUS') }}
        <select
          v-model="statusFilter"
          class="px-2 py-1 text-sm rounded-md border border-n-weak bg-n-alpha-black1 text-n-slate-12"
          @change="onStatusChange"
        >
          <option value="">{{ t('ISSUE_CLUSTERS.FILTERS.ALL') }}</option>
          <option v-for="status in STATUSES" :key="status" :value="status">
            {{ t(`ISSUE_CLUSTERS.STATUS.${status.toUpperCase()}`) }}
          </option>
        </select>
      </label>
      <div class="flex gap-1 ml-auto">
        <Button
          :variant="viewMode === 'list' ? 'solid' : 'outline'"
          color="slate"
          size="sm"
          icon="i-lucide-list"
          :label="t('ISSUE_CLUSTERS.TABS.CLUSTERS')"
          @click="viewMode = 'list'"
        />
        <Button
          :variant="viewMode === 'map' ? 'solid' : 'outline'"
          color="slate"
          size="sm"
          icon="i-lucide-map"
          label="Map"
          @click="viewMode = 'map'"
        />
      </div>
    </div>

    <div v-if="viewMode === 'map'">
      <IssueClusterMap :points="mapPoints" />
    </div>

    <div v-else>
      <p v-if="!clusters.length && !uiFlags.isFetching" class="text-sm text-n-slate-11 py-8 text-center">
        {{ t('ISSUE_CLUSTERS.LIST.EMPTY') }}
      </p>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11 border-b border-n-weak">
            <th class="py-2 pr-4">{{ t('ISSUE_CLUSTERS.LIST.COLUMN.ID') }}</th>
            <th class="py-2 pr-4">{{ t('ISSUE_CLUSTERS.LIST.COLUMN.STATUS') }}</th>
            <th class="py-2 pr-4">{{ t('ISSUE_CLUSTERS.LIST.COLUMN.REPORTS') }}</th>
            <th class="py-2 pr-4">{{ t('ISSUE_CLUSTERS.LIST.COLUMN.PLACE') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="cluster in clusters"
            :key="cluster.id"
            class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-black1"
            @click="openCluster(cluster)"
          >
            <td class="py-2 pr-4 font-medium text-n-slate-12">#{{ cluster.display_id }}</td>
            <td class="py-2 pr-4 text-n-slate-11">
              {{ t(`ISSUE_CLUSTERS.STATUS.${(cluster.status || '').toUpperCase()}`) }}
            </td>
            <td class="py-2 pr-4 text-n-slate-12">{{ cluster.reports_count }}</td>
            <td class="py-2 pr-4 text-n-slate-11">
              {{ t(`ISSUE_CLUSTERS.PLACE_MODE.${(cluster.place_mode || '').toUpperCase()}`) }}
              <span v-if="cluster.zone_value"> · {{ cluster.zone_value }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
